import { toPascalCase } from "../../certificates/components/CertificatesTableRow";
import { Certificate } from "../../certificates/models/certificates.interfaces";
import { api, apiUrl, postData } from "./service";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merges class names into a single string, handling arrays, objects with boolean keys,
 * and resolving Tailwind conflicts. This utility is particularly useful for conditionally
 * applying classes based on application state or logic.
 *
 * Resolves Tailwind class conflicts using `twMerge`, ensuring the last conflicting class wins.
 *
 * @example
 * // Using the `cn` utility
 * const hasError = true;
 * const className = cn("p-4", { "text-red-600": hasError }, "hover:underline");
 * // Result: "p-4 text-red-600 hover:underline"
 *
 * @example
 * // Comparison with `twMerge` alone
 * const hasError = true;
 * const className = twMerge("p-4", hasError ? "text-red-600" : "", "hover:underline");
 * // Result: "p-4 text-red-600 hover:underline"
 *
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// Zero values should be displayed as zero not as NA
export const valOrNa = (val: number, label: string): string => (val || val === 0 ? val + " " + label : "N/A");

/**
 * Capitalizes the first letter of each word in a string
 * @example capitalizeName("hello world") // returns "Hello World"
 * @param name The string to capitalize
 * @returns The capitalized string
 */
export const capitalizeName = (name: string): string => {
  return name
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
//formats a datetime object and also handles null date object
export const getDateOnly = (d?: string | Date | null): string | null =>
  d ? new Date(d).toISOString().split("T")[0] : null; // returns YYYY-MM-D

export const formatDate = (dateString: string | Date) => {
  const date = new Date(dateString);
  if (date.getFullYear() === 1970) {
    return "N/A";
  }
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Sometimes we want to save in the DB calendaristic dates, not absolute ones.
// When dates are converted to json for BE they are converted from local timezone back to UTC.
// Any other client that reads the date can adjust from UTC back to it's timezone.
// This works fine for precise moments in time like the departure date of a vessel.
// When the datepicker creates dates for doc uploads expiry date. It creates them at midnight.
// When converted to json they are UTC adjusted. We can fix this by adjusting back to current timezone in FE.
// However this will not work nice for "calendaristic dates".
// Document expiry date is a calendaristic date. This date should not be coverted back into UTC.
// If we convert back to UTC we then face the issue that other scripts in the Backend that care of
// the expiryDate will receive the date with 1 day difference (midnight - timezone).
// We want the expiry date to always be 18 Sep (for ex), no UTC adjustments.
export function dateToUtc(localDate: Date): Date {
  const GMT0date = new Date(
    Date.UTC(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      localDate.getHours(),
      localDate.getMinutes(),
      localDate.getSeconds(),
    ),
  );

  return GMT0date;
}

export const isExpiringSoon = (expirationDate?: Date | null, minimumDaysToTrigger = 10): boolean => {
  if (!expirationDate) {
    return false;
  }
  const today = new Date();
  const diffDays = Math.ceil((expirationDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
  return diffDays > 0 && diffDays < minimumDaysToTrigger;
};

export const hasRecentlyExpired = (expirationDate?: Date | null, recentlyExpiredDays = 10): boolean => {
  if (!expirationDate) {
    return false;
  }
  const today = new Date();
  const diffDays = Math.ceil(Math.ceil(today.getTime() - expirationDate.getTime()) / (1000 * 3600 * 24));
  return diffDays >= 0 && diffDays <= recentlyExpiredDays;
};

export const hasExpired = (expirationDate?: Date | null): boolean => {
  if (!expirationDate) {
    return false;
  }
  const today = new Date();
  const diffDays = Math.ceil((today.getTime() - expirationDate.getTime()) / (1000 * 3600 * 24));
  return diffDays > 0;
};

export const formatName = (name: string): string => {
  const words = name.toLowerCase().split(" ");
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

interface DownloadOptions {
  url: string;
  fileName?: string;
}

interface DownloadZipOptions {
  urls: string[];
  fileNames?: string[];
}

export async function downloadFile(
  options: DownloadOptions,
  setProgress?: (completion: number) => void,
): Promise<void> {
  const { url, fileName } = options;

  try {
    const queryParams = new URLSearchParams();
    queryParams.append("url", url ?? "");
    queryParams.append("fileName", fileName ?? "");
    queryParams.append("mode", "download");

    const response = await api.get(`${apiUrl}/documents/download?${queryParams}`, {
      responseType: "blob",
      onDownloadProgress: ({ loaded, total }) => {
        setProgress?.(Math.floor((loaded / (total ?? 1)) * 100));
      },
    });

    const contentType = response.headers["content-type"];
    let extension = extractExtension(contentType, url);

    if (!extension) {
      throw new Error("Unsupported file type");
    }

    const blob = new Blob([response.data], { type: contentType });
    const objectURL = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = objectURL;
    link.download = `${fileName}.${extension}`;
    link.click();

    window.URL.revokeObjectURL(link.href);
  } catch (error: any) {
    console.error(`Failed to download file: ${error.message}`);
  }
}

export async function downloadZip(
  options: DownloadZipOptions,
  setProgress?: (completion: number) => void,
): Promise<void> {
  try {
    const response = await api.post(
      `${apiUrl}/documents/download-zip`,
      {
        fileNames: options.fileNames,
        urls: options.urls,
      },
      {
        responseType: "blob",
        onDownloadProgress: (_) => {
          // We don't receive the total size of the archive so we can't compute the ratio.
          // REVIEW this could be guestimated after all... Modal is now parked
          setProgress?.(100);
        },
      },
    );

    const blob: Blob = response.data;
    const link = document.createElement("a");
    const safeBlob = new Blob([blob], { type: blob.type });
    const objectURL = window.URL.createObjectURL(safeBlob);
    const contentDisposition = response.headers["content-disposition"];
    const match = contentDisposition?.match(/filename="?([^"]+)"?/);
    const filename = match?.[1] || "download.zip";
    link.href = objectURL;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
  } catch (error: any) {
    console.error(`Failed to download files as zip: ${error.message}`);
  }
}

export async function openDocumentInNewTab(certificate: Certificate): Promise<void> {
  const fileName =
    `${toPascalCase(certificate.docName)}_${toPascalCase(certificate.vesselName)}_${certificate.imoNumber}`
      .replace(/\//g, "-")
      .trim();

  try {
    const {
      data: { url },
    } = await postData(`documents/presigned-url`, { documentKey: certificate.s3Path });
    const queryParams = new URLSearchParams();
    queryParams.append("url", url);
    queryParams.append("fileName", fileName);
    queryParams.append("mode", "preview");
    const response = await api.get(`${apiUrl}/documents/download?${queryParams}`, {
      responseType: "blob",
    });

    const contentType = response.headers["content-type"];
    let extension = extractExtension(contentType, url);

    // If data type not found in content type we try to recover it from S3 file name (usually .pdf)
    // Mostly legacy Q88 imports have the wrong data type: binary/octet-stream
    if (!extension) {
      try {
        const s3FileName = new URL(url).pathname.substring(1);
        const s3Ext = s3FileName.split(".")[1];
        // eslint-disable-next-line no-console
        console.log("Fallback to extension from s3 path name", s3Ext);
        if (s3Ext) {
          extension = s3Ext;
        }
      } catch (e) {
        // ignore URL parse errors and fall through to error below
      }
    }

    if (!extension) {
      throw new Error("Unsupported file type");
    }

    const blob = new Blob([response.data], { type: contentType!=="binary/octet-stream" ? contentType : "application/pdf" });
    const objectURL = window.URL.createObjectURL(blob);

    if (extension === "pdf") {
      window.open(objectURL, "_blank");
    } else {
      const link = document.createElement("a");
      link.href = objectURL;
      link.download = `${fileName}.${extension}`;
      link.click();
    }

    setTimeout(() => {
      window.URL.revokeObjectURL(objectURL);
    }, 1000);
  } catch (error: any) {
    console.error(`Failed to open document: ${error.message}`);
  }
}

export async function openPdfInNewTab(certificate: Certificate): Promise<void> {
  try {
    // Generate presigned URL for the PDF
    const {
      data: { url },
    } = await postData(`documents/presigned-url`, { documentKey: certificate.s3Path });

    // Build query parameters for the download endpoint
    const queryParams = new URLSearchParams();
    queryParams.append("url", url ?? "");
    queryParams.append("fileName", certificate.fileName ?? "");

    const response = await api.get(`${apiUrl}/documents/download?${queryParams}`, {
      responseType: "blob",
    });

    const blob: Blob = response.data;
    const safeBlob = new Blob([blob], { type: "application/pdf" });
    const objectURL = window.URL.createObjectURL(safeBlob);

    window.open(objectURL, "_blank");

    setTimeout(() => {
      window.URL.revokeObjectURL(objectURL);
    }, 1000);
  } catch (error: any) {
    console.error(`Failed to open PDF: ${error.message}`);
  }
}

// Needed for creating emails, they need to have an UUID.
// Could be potentially replaced with a lib.
export function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function extractExtension(contentType: any, url: string) {
  let extension =
    contentType === "application/pdf"
      ? "pdf"
      : contentType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ? "docx"
        : contentType === "binary/octet-stream"
         ? "pdf"
          : null;

  // If data type not found in content type we try to recover it from S3 file name (usually .pdf)
  // Mostly legacy Q88 imports have the wrong data type: binary/octet-stream
  if (!extension) {
    try {
      const s3FileName = new URL(url).pathname.substring(1);
      const s3Ext = s3FileName.split(".")[1];
      // eslint-disable-next-line no-console
      console.log("Fallback to extension from s3 path name", s3Ext);
      if (s3Ext) {
        extension = s3Ext;
      }
    } catch (e) {
      // ignore URL parse errors and fall through to error below
    }
  }
  return extension;
}
